/**
 * The edit model of the section editor and the operations on it.
 *
 * The model is plain JSON and small: slot values the marketer changed
 * and, only when it differs from the proposal, the layout (cut, order,
 * hidden/removed/duplicated sections). The original HTML is never part
 * of it. Every operation is pure: it returns a new model or a typed
 * refusal, and never changes its arguments.
 */
import { sectionsForCut, type SectionDocument, type SectionInfo, type Slot } from './analyse.js';
import {
  checkAltValue,
  checkHrefValue,
  checkSrcValue,
  checkTextValue,
  type ValueRefusal,
} from './text.js';

export const SECTION_MODEL_VERSION = 1;

/** A changed slot. Only the changed fields are stored. `o: 1` writes the Outlook duplicates of an image even when the image itself is unchanged. */
export interface SlotEdit {
  text?: string;
  src?: string;
  alt?: string;
  href?: string;
  o?: 1;
}

/** One section in output order. `d`: duplicate number (absent for the original), `h`: hidden, `x`: removed. */
export interface OrderEntry {
  s: string;
  d?: number;
  h?: 1;
  x?: 1;
}

export interface SectionLayout {
  /** Ids of the blocks that start a section, in source order. */
  cut: string[];
  /** Every section of the cut exactly once, plus duplicates, in output order. */
  order: OrderEntry[];
  /** Next duplicate number. */
  n: number;
}

export interface SectionEditModel {
  v: 1;
  /** Keyed by slot id, or `slotId~d` for a slot inside duplicate number `d`. */
  slots?: Record<string, SlotEdit>;
  /** Absent while the layout is the proposed one. */
  layout?: SectionLayout;
  /** Names the marketer gave sections, keyed by section id. */
  names?: Record<string, string>;
}

export function emptySectionModel(): SectionEditModel {
  return { v: 1 };
}

export type SectionOpErrorCode =
  | 'unknown_slot'
  | 'unknown_section'
  | 'slot_protected'
  | 'wrong_slot_kind'
  | 'section_locked'
  | 'footer_must_stay_last'
  | 'crosses_locked_section'
  | 'not_neighbours'
  | 'has_duplicates'
  | 'section_hidden'
  | 'cannot_split_here'
  | 'no_row_structure'
  | 'invalid_position'
  | 'invalid_name'
  | ValueRefusal;

export interface SectionOpError {
  code: SectionOpErrorCode;
  message: string;
}

export type SectionOpResult =
  | {
      ok: true;
      model: SectionEditModel;
      /** Id of the section instance an operation created. */ created?: string;
    }
  | { ok: false; error: SectionOpError };

function fail(code: SectionOpErrorCode, message: string): SectionOpResult {
  return { ok: false, error: { code, message } };
}

/** What could not be applied from a saved model, for example after the original was replaced. */
export interface UnmappedEdit {
  code: 'slot_missing' | 'slot_refused' | 'cut_changed' | 'order_reset' | 'name_missing';
  /** Slot instance id or section id the entry was stored under. */
  id?: string;
  message: string;
}

export interface SectionInstance {
  /** `sectionId`, or `sectionId~d` for a duplicate. */
  id: string;
  section: SectionInfo;
  /** Duplicate number, or `null` for the original. */
  dup: number | null;
  hidden: boolean;
  removed: boolean;
}

export interface ResolvedLayout {
  /** Row sections under the cut in force, in source order. */
  sections: SectionInfo[];
  /** Instances in output order, removed ones included. */
  order: SectionInstance[];
  /** Next duplicate number. */
  n: number;
  issues: UnmappedEdit[];
}

export function instanceId(sectionId: string, dup: number | null | undefined): string {
  return dup === null || dup === undefined ? sectionId : `${sectionId}~${dup}`;
}

/** Splits `id~3` into the base id and the duplicate number. */
export function parseInstanceId(id: string): { base: string; dup: number | null } {
  const match = /^(.*)~(\d{1,6})$/.exec(id);
  return match ? { base: match[1]!, dup: Number(match[2]) } : { base: id, dup: null };
}

function isLocked(section: SectionInfo): boolean {
  return section.kind !== 'movable';
}

function startsFor(doc: SectionDocument, cut: readonly string[]): number[] | null {
  if (cut.length === 0 || new Set(cut).size !== cut.length) return null;
  const starts: number[] = [];
  for (const id of cut) {
    const index = doc.blocks.findIndex((b) => b.id === id);
    if (index === -1) return null;
    if (index !== 0 && !doc.blocks[index]!.canStart) return null;
    if (starts.length > 0 && index <= starts[starts.length - 1]!) return null;
    starts.push(index);
  }
  return starts[0] === 0 ? starts : null;
}

/**
 * Why `order` is not a legal arrangement of `sections`, or `null` when
 * it is. Locked sections keep their place: a movable section may only
 * be rearranged among the movable sections between the same two locked
 * ones. With the footer locked and last, "nothing below the footer"
 * follows from this.
 */
function orderProblem(
  sections: SectionInfo[],
  order: readonly OrderEntry[],
): { code: SectionOpErrorCode; message: string } | null {
  const byId = new Map(sections.map((s, i) => [s.id, i]));
  const zone: number[] = [];
  let locks = 0;
  for (const section of sections) {
    zone.push(locks);
    if (isLocked(section)) locks += 1;
  }
  const originals = new Set<string>();
  const dups = new Set<number>();
  let current = 0;
  for (const entry of order) {
    const index = byId.get(entry.s);
    if (index === undefined) {
      return { code: 'unknown_section', message: `There is no section "${entry.s}".` };
    }
    const section = sections[index]!;
    if (entry.d === undefined) {
      if (originals.has(entry.s)) {
        return { code: 'unknown_section', message: 'A section is listed twice.' };
      }
      originals.add(entry.s);
    } else {
      if (!Number.isInteger(entry.d) || entry.d < 1 || dups.has(entry.d)) {
        return { code: 'unknown_section', message: 'A duplicate has an invalid number.' };
      }
      dups.add(entry.d);
    }
    if (isLocked(section)) {
      if (entry.d !== undefined || entry.h !== undefined || entry.x !== undefined) {
        return {
          code: 'section_locked',
          message: `"${section.name}" is locked: it cannot be hidden, duplicated or removed.`,
        };
      }
      if (zone[index] !== current) {
        return section.kind === 'locked-footer'
          ? { code: 'footer_must_stay_last', message: 'The footer must stay last.' }
          : { code: 'crosses_locked_section', message: `"${section.name}" is locked in place.` };
      }
      current += 1;
    } else if (zone[index] !== current) {
      const footer = sections[sections.length - 1]!;
      return zone[index]! < current && footer.kind === 'locked-footer' && current === locks
        ? { code: 'footer_must_stay_last', message: 'Nothing can be placed below the footer.' }
        : {
            code: 'crosses_locked_section',
            message: `"${section.name}" cannot be moved past a locked section.`,
          };
    }
  }
  if (originals.size !== sections.length) {
    return { code: 'unknown_section', message: 'A section is missing from the order.' };
  }
  return null;
}

function defaultOrder(sections: SectionInfo[]): OrderEntry[] {
  return sections.map((s) => ({ s: s.id }));
}

/**
 * The layout in force for `model` on `doc`. A stored layout that no
 * longer fits the document (the original was replaced) is not half
 * applied: the proposal is used instead and the reason is reported.
 */
export function resolveLayout(doc: SectionDocument, model: SectionEditModel): ResolvedLayout {
  const issues: UnmappedEdit[] = [];
  const proposedStarts = startsFor(doc, doc.proposedCut) ?? [];
  let starts = proposedStarts;
  let entries: OrderEntry[] | null = null;
  let n = 1;
  const layout = doc.structure === 'rows' ? model.layout : undefined;

  if (layout && Array.isArray(layout.cut) && Array.isArray(layout.order)) {
    const own = startsFor(doc, layout.cut);
    if (!own) {
      issues.push({
        code: 'cut_changed',
        message:
          'The saved sections no longer match the mail. The proposed sections and order are used.',
      });
    } else {
      starts = own;
      const sections = sectionsForCut(doc, starts);
      const problem = orderProblem(sections, layout.order);
      if (problem) {
        issues.push({
          code: 'order_reset',
          message: `The saved order no longer fits the mail (${problem.message}) The original order is used.`,
        });
      } else {
        entries = layout.order;
        const highest = layout.order.reduce((max, e) => Math.max(max, e.d ?? 0), 0);
        n = Math.max(Number.isInteger(layout.n) ? layout.n : 1, highest + 1);
      }
    }
  } else if (model.layout !== undefined && doc.structure === 'rows') {
    issues.push({ code: 'cut_changed', message: 'The saved layout is not readable.' });
  } else if (model.layout !== undefined) {
    issues.push({
      code: 'cut_changed',
      message:
        'The mail no longer has sections that can be arranged. The saved layout is not used.',
    });
  }

  const sections = sectionsForCut(doc, starts);
  const byId = new Map(sections.map((s) => [s.id, s]));
  const order = (entries ?? defaultOrder(sections)).map((entry): SectionInstance => {
    const section = byId.get(entry.s)!;
    return {
      id: instanceId(entry.s, entry.d),
      section,
      dup: entry.d ?? null,
      hidden: entry.h === 1,
      removed: entry.x === 1,
    };
  });
  return { sections, order, n, issues };
}

function toEntries(order: SectionInstance[]): OrderEntry[] {
  return order.map((inst) => ({
    s: inst.section.id,
    ...(inst.dup === null ? {} : { d: inst.dup }),
    ...(inst.hidden ? { h: 1 as const } : {}),
    ...(inst.removed ? { x: 1 as const } : {}),
  }));
}

/** Stores a layout on the model; a layout equal to the proposal is not stored at all. */
function withLayout(
  doc: SectionDocument,
  model: SectionEditModel,
  cut: string[],
  order: OrderEntry[],
  n: number,
): SectionEditModel {
  const isDefault =
    cut.length === doc.proposedCut.length &&
    cut.every((id, i) => id === doc.proposedCut[i]) &&
    order.length === cut.length &&
    order.every(
      (e, i) => e.s === cut[i] && e.d === undefined && e.h === undefined && e.x === undefined,
    );
  const { layout: _old, ...rest } = model;
  return isDefault ? rest : { ...rest, layout: { cut, order, n } };
}

function withSlots(model: SectionEditModel, slots: Record<string, SlotEdit>): SectionEditModel {
  const { slots: _old, ...rest } = model;
  return Object.keys(slots).length === 0 ? rest : { ...rest, slots };
}

interface LayoutContext {
  resolved: ResolvedLayout;
  cut: string[];
}

function context(doc: SectionDocument, model: SectionEditModel): LayoutContext | SectionOpResult {
  if (doc.structure !== 'rows') {
    return fail('no_row_structure', 'This mail has no sections that can be arranged.');
  }
  const resolved = resolveLayout(doc, model);
  return { resolved, cut: resolved.sections.map((s) => s.id) };
}

function findInstance(resolved: ResolvedLayout, id: string): number {
  return resolved.order.findIndex((inst) => inst.id === id && !inst.removed);
}

function lockedError(inst: SectionInstance, verb: string): SectionOpResult {
  return fail('section_locked', `"${inst.section.name}" is locked and cannot be ${verb}.`);
}

// --- slot values ---------------------------------------------------------

export type SlotValueInput =
  { text: string } | { src?: string; alt?: string; syncOutlook?: boolean } | { href: string };

/** The slot a slot-instance id points at, when both the slot and its section instance exist. */
export function findSlotInstance(
  doc: SectionDocument,
  resolved: ResolvedLayout,
  slotInstanceId: string,
): Slot | null {
  const { base, dup } = parseInstanceId(slotInstanceId);
  const slot = doc.slots.find((s) => s.id === base);
  if (!slot) return null;
  if (dup === null) return slot;
  if (slot.chunk < 0) return null;
  const owner = resolved.order.find(
    (inst) =>
      inst.dup === dup &&
      !inst.removed &&
      inst.section.rows !== null &&
      slot.chunk >= inst.section.rows.first &&
      slot.chunk <= inst.section.rows.last,
  );
  return owner ? slot : null;
}

/** Checks a value for a slot and returns the edit to store (`{}` when it equals the original). */
export function checkSlotEdit(
  slot: Slot,
  input: SlotValueInput,
): { ok: true; edit: SlotEdit } | { ok: false; error: SectionOpError } {
  const refuse = (code: SectionOpErrorCode, message: string) =>
    ({ ok: false, error: { code, message } }) as const;
  if (slot.protected) {
    return refuse(
      'slot_protected',
      slot.protected === 'unsubscribe'
        ? 'The unsubscribe link cannot be changed.'
        : 'The sender details cannot be changed here. They come from the Brand kit or the original mail.',
    );
  }
  const keys = Object.keys(input).filter(
    (k) => (input as Record<string, unknown>)[k] !== undefined,
  );
  const allowed =
    slot.kind === 'text'
      ? ['text']
      : slot.kind === 'image'
        ? ['src', 'alt', 'syncOutlook']
        : ['href'];
  if (keys.length === 0 || keys.some((k) => !allowed.includes(k))) {
    return refuse('wrong_slot_kind', `A ${slot.kind} slot takes ${allowed.join(', ')}.`);
  }
  const values = input as {
    text?: unknown;
    src?: unknown;
    alt?: unknown;
    href?: unknown;
    syncOutlook?: unknown;
  };
  for (const key of ['text', 'src', 'alt', 'href'] as const) {
    if (values[key] !== undefined && typeof values[key] !== 'string') {
      return refuse('wrong_slot_kind', `The value of ${key} must be text.`);
    }
  }

  const edit: SlotEdit = {};
  if (slot.kind === 'text') {
    const checked = checkTextValue(values.text as string);
    if (!checked.ok) return refuse(checked.code, checked.message);
    if (checked.value !== slot.value) edit.text = checked.value;
  } else if (slot.kind === 'image') {
    if (values.src !== undefined) {
      const checked = checkSrcValue(values.src as string);
      if (!checked.ok) return refuse(checked.code, checked.message);
      if (checked.value !== slot.src) edit.src = checked.value;
    }
    if (values.alt !== undefined) {
      const checked = checkAltValue(values.alt as string);
      if (!checked.ok) return refuse(checked.code, checked.message);
      if (checked.value !== slot.alt) edit.alt = checked.value;
    }
    if (values.syncOutlook === true && slot.twins.length > 0) edit.o = 1;
  } else {
    const checked = checkHrefValue(values.href as string);
    if (!checked.ok) return refuse(checked.code, checked.message);
    if (checked.value !== slot.href) edit.href = checked.value;
  }
  return { ok: true, edit };
}

/** Turns a stored edit back into the input form, for re-checking at render time. */
export function editAsInput(slot: Slot, edit: SlotEdit): SlotValueInput {
  if (slot.kind === 'text') return { text: edit.text as string };
  if (slot.kind === 'image') {
    return {
      ...(edit.src === undefined ? {} : { src: edit.src }),
      ...(edit.alt === undefined ? {} : { alt: edit.alt }),
      ...(edit.o === undefined ? {} : { syncOutlook: true }),
    };
  }
  return { href: edit.href as string };
}

/**
 * Sets a slot's value. For an image, the fields not given keep their
 * current (already edited) value. A value equal to the original removes
 * the edit, so undoing by hand returns to the empty model.
 */
export function setSlot(
  doc: SectionDocument,
  model: SectionEditModel,
  slotInstanceId: string,
  input: SlotValueInput,
): SectionOpResult {
  const slot = findSlotInstance(doc, resolveLayout(doc, model), slotInstanceId);
  if (!slot) return fail('unknown_slot', `There is no slot "${slotInstanceId}".`);
  const previous = model.slots?.[slotInstanceId];
  const merged: SlotValueInput =
    slot.kind === 'image' && previous
      ? { ...editAsInput(slot, previous), ...(input as object) }
      : input;
  const checked = checkSlotEdit(slot, merged);
  if (!checked.ok) return checked;
  const slots = { ...(model.slots ?? {}) };
  if (Object.keys(checked.edit).length === 0) delete slots[slotInstanceId];
  else slots[slotInstanceId] = checked.edit;
  return { ok: true, model: withSlots(model, slots) };
}

/** Removes the edit of one slot, back to the original value. */
export function resetSlot(
  doc: SectionDocument,
  model: SectionEditModel,
  slotInstanceId: string,
): SectionOpResult {
  if (
    !findSlotInstance(doc, resolveLayout(doc, model), slotInstanceId) &&
    !model.slots?.[slotInstanceId]
  ) {
    return fail('unknown_slot', `There is no slot "${slotInstanceId}".`);
  }
  const slots = { ...(model.slots ?? {}) };
  delete slots[slotInstanceId];
  return { ok: true, model: withSlots(model, slots) };
}

// --- layout operations ---------------------------------------------------

/**
 * Moves a section to position `toIndex` among the sections that are not
 * removed (0 = first). Refused when the section is locked or the move
 * would cross a locked section or put anything below the footer.
 */
export function moveSection(
  doc: SectionDocument,
  model: SectionEditModel,
  sectionInstanceId: string,
  toIndex: number,
): SectionOpResult {
  const ctx = context(doc, model);
  if ('ok' in ctx) return ctx;
  const { resolved, cut } = ctx;
  const from = findInstance(resolved, sectionInstanceId);
  if (from === -1) return fail('unknown_section', `There is no section "${sectionInstanceId}".`);
  const inst = resolved.order[from]!;
  if (isLocked(inst.section)) {
    return inst.section.kind === 'locked-footer'
      ? fail('footer_must_stay_last', 'The footer must stay last.')
      : lockedError(inst, 'moved');
  }
  const others = resolved.order.filter((_, i) => i !== from);
  const present = others.filter((o) => !o.removed);
  if (!Number.isInteger(toIndex) || toIndex < 0 || toIndex > present.length) {
    return fail('invalid_position', `Position ${toIndex} does not exist.`);
  }
  const anchor = present[toIndex];
  const at = anchor ? others.indexOf(anchor) : others.length;
  const next = [...others.slice(0, at), inst, ...others.slice(at)];
  const entries = toEntries(next);
  const problem = orderProblem(resolved.sections, entries);
  if (problem) return fail(problem.code, problem.message);
  return { ok: true, model: withLayout(doc, model, cut, entries, resolved.n) };
}

function setHidden(
  doc: SectionDocument,
  model: SectionEditModel,
  sectionInstanceId: string,
  hidden: boolean,
): SectionOpResult {
  const ctx = context(doc, model);
  if ('ok' in ctx) return ctx;
  const { resolved, cut } = ctx;
  const index = findInstance(resolved, sectionInstanceId);
  if (index === -1) return fail('unknown_section', `There is no section "${sectionInstanceId}".`);
  const inst = resolved.order[index]!;
  if (isLocked(inst.section)) return lockedError(inst, 'hidden');
  const next = resolved.order.map((o, i) => (i === index ? { ...o, hidden } : o));
  return { ok: true, model: withLayout(doc, model, cut, toEntries(next), resolved.n) };
}

/** Leaves the section out of the rendered mail but keeps it and its edits. */
export function hideSection(
  doc: SectionDocument,
  model: SectionEditModel,
  id: string,
): SectionOpResult {
  return setHidden(doc, model, id, true);
}

export function showSection(
  doc: SectionDocument,
  model: SectionEditModel,
  id: string,
): SectionOpResult {
  return setHidden(doc, model, id, false);
}

/** Adds a copy directly below the section. The copy starts with the same slot values and has its own slot ids (`slotId~d`). */
export function duplicateSection(
  doc: SectionDocument,
  model: SectionEditModel,
  sectionInstanceId: string,
): SectionOpResult {
  const ctx = context(doc, model);
  if ('ok' in ctx) return ctx;
  const { resolved, cut } = ctx;
  const index = findInstance(resolved, sectionInstanceId);
  if (index === -1) return fail('unknown_section', `There is no section "${sectionInstanceId}".`);
  const inst = resolved.order[index]!;
  if (isLocked(inst.section)) return lockedError(inst, 'duplicated');
  const dup = resolved.n;
  const copy: SectionInstance = {
    id: instanceId(inst.section.id, dup),
    section: inst.section,
    dup,
    hidden: false,
    removed: false,
  };
  const next = [...resolved.order.slice(0, index + 1), copy, ...resolved.order.slice(index + 1)];
  const slots = { ...(model.slots ?? {}) };
  for (const slotId of inst.section.slotIds) {
    const source = slots[instanceId(slotId, inst.dup)];
    if (source) slots[instanceId(slotId, dup)] = { ...source };
  }
  return {
    ok: true,
    model: withSlots(withLayout(doc, model, cut, toEntries(next), dup + 1), slots),
    created: copy.id,
  };
}

/** Removes a section. An original can be brought back with {@link restoreSection}; a removed duplicate is gone with its edits. */
export function removeSection(
  doc: SectionDocument,
  model: SectionEditModel,
  sectionInstanceId: string,
): SectionOpResult {
  const ctx = context(doc, model);
  if ('ok' in ctx) return ctx;
  const { resolved, cut } = ctx;
  const index = findInstance(resolved, sectionInstanceId);
  if (index === -1) return fail('unknown_section', `There is no section "${sectionInstanceId}".`);
  const inst = resolved.order[index]!;
  if (isLocked(inst.section)) return lockedError(inst, 'removed');
  if (inst.dup === null) {
    const next = resolved.order.map((o, i) =>
      i === index ? { ...o, hidden: false, removed: true } : o,
    );
    return { ok: true, model: withLayout(doc, model, cut, toEntries(next), resolved.n) };
  }
  const next = resolved.order.filter((_, i) => i !== index);
  const slots = { ...(model.slots ?? {}) };
  for (const slotId of inst.section.slotIds) delete slots[instanceId(slotId, inst.dup)];
  return {
    ok: true,
    model: withSlots(withLayout(doc, model, cut, toEntries(next), resolved.n), slots),
  };
}

/** Brings a removed original section back where it was. */
export function restoreSection(
  doc: SectionDocument,
  model: SectionEditModel,
  sectionId: string,
): SectionOpResult {
  const ctx = context(doc, model);
  if ('ok' in ctx) return ctx;
  const { resolved, cut } = ctx;
  const index = resolved.order.findIndex((o) => o.id === sectionId && o.removed);
  if (index === -1) return fail('unknown_section', `There is no removed section "${sectionId}".`);
  const next = resolved.order.map((o, i) => (i === index ? { ...o, removed: false } : o));
  return { ok: true, model: withLayout(doc, model, cut, toEntries(next), resolved.n) };
}

function plainProblem(resolved: ResolvedLayout, sectionId: string): SectionOpResult | null {
  const mine = resolved.order.filter((o) => o.section.id === sectionId);
  if (mine.some((o) => o.dup !== null)) {
    return fail('has_duplicates', 'Remove the copies of this section first.');
  }
  if (mine.some((o) => o.hidden || o.removed)) {
    return fail('section_hidden', 'Show or restore this section first.');
  }
  return null;
}

/**
 * Merges two sections that follow each other in the mail into one. Both
 * must be plain (not hidden, removed or duplicated) and still next to
 * each other in the current order.
 */
export function mergeSections(
  doc: SectionDocument,
  model: SectionEditModel,
  firstId: string,
  secondId: string,
): SectionOpResult {
  const ctx = context(doc, model);
  if ('ok' in ctx) return ctx;
  const { resolved, cut } = ctx;
  let a = cut.indexOf(firstId);
  let b = cut.indexOf(secondId);
  if (a === -1 || b === -1) {
    return fail('unknown_section', `There is no section "${a === -1 ? firstId : secondId}".`);
  }
  if (a > b) [a, b] = [b, a];
  const upper = cut[a]!;
  const lower = cut[b]!;
  const posUpper = resolved.order.findIndex((o) => o.id === upper);
  const posLower = resolved.order.findIndex((o) => o.id === lower);
  const plain = plainProblem(resolved, upper) ?? plainProblem(resolved, lower);
  if (b !== a + 1 || (plain === null && posLower !== posUpper + 1)) {
    return fail('not_neighbours', 'Only two sections directly after each other can be merged.');
  }
  if (plain) return plain;
  const nextCut = cut.filter((id) => id !== lower);
  const entries = toEntries(resolved.order.filter((o) => o.section.id !== lower));
  const starts = startsFor(doc, nextCut)!;
  const problem = orderProblem(sectionsForCut(doc, starts), entries);
  /* v8 ignore next -- merging neighbours cannot break the order; kept as a guard */
  if (problem) return fail(problem.code, problem.message);
  const { names: oldNames, ...rest } = model;
  const names = { ...(oldNames ?? {}) };
  delete names[lower];
  const base = Object.keys(names).length === 0 ? rest : { ...rest, names };
  return { ok: true, model: withLayout(doc, base, nextCut, entries, resolved.n) };
}

/**
 * Splits a section in two at a row boundary: `blockId` (a content row of
 * the section, not its first) becomes the first row of a new section
 * directly below. Refused where rows are tied together and inside the
 * footer below its first row.
 */
export function splitSection(
  doc: SectionDocument,
  model: SectionEditModel,
  sectionId: string,
  blockId: string,
): SectionOpResult {
  const ctx = context(doc, model);
  if ('ok' in ctx) return ctx;
  const { resolved, cut } = ctx;
  const section = resolved.sections.find((s) => s.id === sectionId);
  if (!section?.blocks) return fail('unknown_section', `There is no section "${sectionId}".`);
  const block = doc.blocks.findIndex((b) => b.id === blockId);
  if (block <= section.blocks.first || block > section.blocks.last) {
    return fail('cannot_split_here', 'That row is not a row inside this section.');
  }
  if (!doc.blocks[block]!.canStart) {
    return fail(
      'cannot_split_here',
      block > doc.footerBlock && doc.footerBlock !== -1
        ? 'The footer cannot be split below its first row.'
        : 'These rows are tied together and cannot be split.',
    );
  }
  const plain = plainProblem(resolved, sectionId);
  if (plain) return plain;
  const nextCut = [...cut];
  nextCut.splice(cut.indexOf(sectionId) + 1, 0, blockId);
  const at = resolved.order.findIndex((o) => o.id === sectionId);
  const entries = toEntries(resolved.order);
  entries.splice(at + 1, 0, { s: blockId });
  const problem = orderProblem(sectionsForCut(doc, startsFor(doc, nextCut)!), entries);
  /* v8 ignore next -- a split keeps both halves in place; kept as a guard */
  if (problem) return fail(problem.code, problem.message);
  return { ok: true, model: withLayout(doc, model, nextCut, entries, resolved.n) };
}

export const MAX_SECTION_NAME_LENGTH = 60;

/** Gives a section the marketer's own name; an empty name returns to the proposed one. */
export function renameSection(
  doc: SectionDocument,
  model: SectionEditModel,
  sectionId: string,
  name: string,
): SectionOpResult {
  const known =
    resolveLayout(doc, model).sections.some((s) => s.id === sectionId) ||
    doc.sections.some((s) => s.id === sectionId);
  if (!known) return fail('unknown_section', `There is no section "${sectionId}".`);
  const clean = name.replace(/\s+/g, ' ').trim();
  if (clean.length > MAX_SECTION_NAME_LENGTH) {
    return fail(
      'invalid_name',
      `A section name can be at most ${MAX_SECTION_NAME_LENGTH} characters.`,
    );
  }
  const { names: oldNames, ...rest } = model;
  const names = { ...(oldNames ?? {}) };
  if (clean === '') delete names[sectionId];
  else names[sectionId] = clean;
  return { ok: true, model: Object.keys(names).length === 0 ? rest : { ...rest, names } };
}
